import {
  isKorpoRegisterMode,
  isTransferRegisterMode,
  PRODUCTS,
  ProductType,
  RegisterMode,
} from 'cart-model';
import { useRouter, useTrans } from 'common-react';
import { PurchasableFund, UnitCategoryCode } from 'funds-model';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import {
  FundList,
  RenderWhenVisible,
  ROI,
  useSelect,
  DropdownMulti,
  fundListOptions, Autocomplete,
  createGenerateAutoTestId,
  AutoTestIdPrefix,
  AlertBox,
} from 'ui-components';
import { Actions, StandardSortPanel } from 'ui-components/src/FundList/FundList';
import FundListFilter from 'ui-components/src/FundList/FundListFilter';
import { filterInitialState, useFilterState } from 'ui-components/src/FundList/useFilterState';
import { useFundListFilter } from 'ui-components/src/FundList/useFundListFilter';
import { useSortState } from 'ui-components/src/FundList/useSortState';
import { logExpertViewProductList, logQuotesChangeRate } from '../../model/events';
import { useFundsVisibilityEventLogger } from '../../model/FundEventsContext';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import useGetAvailableFundsByUnitCategory from '../../model/useGetAvailableFundsByUnitCategory';
import { useAddCartAction } from '../Cart/AddToCartButton/useAddCartAction';
import CartQuantityBar from '../Cart/CartQuantityBar/CartQuantityBar';
import FundDetailsButtonWithDialog from '../FundDetailsButtonWithDialog/FundDetailsButtonWithDialog';
import { FundSmallChart } from '../FundSmallChart/FundSmallChart';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import Screen from '../ProductWizard/Screen';

import useStyles from './SelectFunds.style';


const renderFundChart = (
    fund: PurchasableFund,
    unitCategoryCode: UnitCategoryCode,
    rateKey: ROI,
    options: {
      isMobile?: boolean;
      fundBoxClassName?: string;
      handleSelectMobileRateKey?: (event: React.ChangeEvent<{ value: unknown }>) => void;
    },
): React.ReactNode => (
    <RenderWhenVisible>
      <FundSmallChart
          height={40}
          fund={fund}
          unitCategory={unitCategoryCode}
          changeRateKey={rateKey}
          noChange={_.isNil(_.get(fund.quotesForUnitCode, [unitCategoryCode, 'changes', rateKey]))}
          changeFromFund={_.get(fund.quotesForUnitCode, [unitCategoryCode, 'changes', rateKey])}
      />
    </RenderWhenVisible>
);

const SelectFunds: React.FC = () => {
  const dispatch = useDispatch();
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const initialFilter = useSelector((state: ProductWizardState) => state.uiState.initialFilter);
  const apiState = useSelector((state: ProductWizardState) => state.apiState);
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const korpoData = useSelector((state: ProductWizardState) => state.cartState.korpoData);
  const isPensionProcess = useSelector((state: ProductWizardState) => state.uiState.isPensionProcess);
  const testAutomationEnabled = useSelector((state: ProductWizardState) => state.config.testAutomationEnabled);
  const getAvailableFundsForUnitCategory = useGetAvailableFundsByUnitCategory();
  const generateAutoTestId = createGenerateAutoTestId(testAutomationEnabled);
  const risks = apiState.risks;
  const fundTypes = apiState.fundTypes;
  const umbrellas = apiState.umbrellas;

  const isTransfer = isTransferRegisterMode(registerMode);
  const isKorpo = isKorpoRegisterMode(registerMode);

  const { logFundVisibilityIfShownFirstTime } = useFundsVisibilityEventLogger(Screen.SELECT_FUNDS);

  const [t] = useTranslation('select-funds');
  const [tProductWizard] = useTranslation('product-wizard');
  const { trans } = useTrans('select-funds');
  const c = useStyles({});
  const addToCart = useAddCartAction(Screen.SELECT_FUNDS, isTransfer);
  const cartFunds = useSelector((state: ProductWizardState) => state.cartState.cartFunds);

  const { location } = useRouter();
  useEffect(() => logExpertViewProductList(location.state &&
          // @ts-ignore
          location.state.currentScreenCode),
      []);

  useEffect(() => {
    productType && dispatch(productWizardActions.retainFundsByProductType(productType));
  }, [productType]);

  const { filters, actions, getFilteredFunds, isInitialFilter } = useFilterState({
    ...filterInitialState,
    fundType: initialFilter.fundTypes.map(value => value.name),
    horizon: initialFilter.horizon ? [fundListOptions.horizon.find(value => value.filter.number === initialFilter.horizon!.id)?.filter.number] : [],
    risk: initialFilter.risks.map(value => value.name),
    search: initialFilter.term,
    umbrella: initialFilter.umbrellas.map(value => value.name),
  });

  const { sortConfig, requestSort, getSortedFunds, isInitialSort } = useSortState();
  const [rateKey, handleSelectRateKey] = useSelect(ROI.M12);
  const handleSelectRateKeyWithLogEvent = (event: React.ChangeEvent<{ value: unknown }>) => {
    handleSelectRateKey(event);
    logQuotesChangeRate(event.target.value as string)
  }

  const unitCategoryCode = isKorpo && korpoData != null ? korpoData.unitCategoryCode : (productType && PRODUCTS[productType].unitCategory);

  const isFundVisibleForUnitCategory = () => true;

  const allowedFunds = unitCategoryCode ? getAvailableFundsForUnitCategory(unitCategoryCode) : [];
  const sortedFunds = unitCategoryCode && getSortedFunds(allowedFunds as PurchasableFund[], sortConfig, rateKey, unitCategoryCode);

  const visibleFunds = getFilteredFunds(sortedFunds, filters, true);
  const filterConfig = useFundListFilter(actions,
      filters,
      getFilteredFunds(sortedFunds, filters, false),
      sortedFunds,
      undefined,
      {
        risks: risks as any,
        fundTypes: fundTypes as any,
        umbrellas: umbrellas as any,
      });

  const grouped = _.groupBy(visibleFunds, value => isInitialFilter &&
      isPensionProcess &&
      isInitialSort &&
      value.fundType.name === 'cyklu życia');
  const specialPensionFunds = grouped['true'];
  const otherFunds = grouped['false'] ?? [];

  const onSelectFund = useCallback((fund: PurchasableFund, setIsButtonLoading?: (value: boolean) => void) => addToCart(
      fund), [addToCart]);

  if (productType === undefined || unitCategoryCode === undefined) {
    return <Redirect to="/"/>
  }

  const getSubtitleKey = (): string => {
    switch (registerMode) {
      case RegisterMode.OPEN_KORPO:
        return `open-${productType.toLowerCase()}-korpo`;
      case RegisterMode.EXT_TRANSFER_KORPO:
        return `transfer-${productType.toLowerCase()}-from-other-to-korpo`;
      case RegisterMode.PPE_TRANSFER_PAYMENT_KORPO:
        return `transfer-${productType.toLowerCase()}-from-ppe`;
      case RegisterMode.TRANSFER:
        return `transfer-${productType.toLowerCase()}-from-other`;
      case RegisterMode.PPE_TRANSFER_PAYMENT:
        return `transfer-${productType.toLowerCase()}-from-ppe`;
      case RegisterMode.OPEN:
        return `open-${productType.toLowerCase()}`;
      default:
        return Screen.SELECT_FUNDS.code;
    }
  };

  const fundListConfig = {
    rateKey: rateKey,
    unitCategoryCode,
    showInadequacy: false,
    isFundVisibleForUnitCategory,
    fundsState: {
      risks: risks as any,
      fundTypes: fundTypes as any,
      umbrellas: umbrellas as any,
    },
    renderFundChart: renderFundChart,
    selectedFundIds: cartFunds.map(cf => cf.fund.id),
    onSelectFund,
    onFundVisibility: (fund: PurchasableFund, index: number) => logFundVisibilityIfShownFirstTime(fund.id, productType, index),
    generateAutoTestId,
    clearFiltersHandler: filterConfig.clearFilters,
  };

  const renderSubtitle = () => {
    if (productType === ProductType.funds) return undefined;
    return tProductWizard(`title.${getSubtitleKey()}`);
  }
  const filterTypeLabelTextKey = 'table.filter.fund-type-label';
  const filterAutocompleteLabelTextKey = 'fund-list.autocomplete-placeholder';
  const filterRiskLabelTextKey = 'table.filter.risk-label';
  const filterUmbrellaLabelTextKey = 'table.filter.umbrella-label';
  const filterHorizonLabelTextKey = 'table.filter.horizon-label';

  return (
    <>
      <div className={c.root}>
        <ProductWizardTitle
          titleKey='select-funds'
          subtitle={renderSubtitle()}
        />
        <div className={c.root}>
          <FundListFilter
              className={c.fundListFilter}
              hasUnitCategorySelect={false}
              resultsNumber={visibleFunds ? visibleFunds.length : 0}
              actions={actions as Actions}
              filterConfig={filterConfig}
              showWalletCheckbox={false}
              renderFundTypeDropdown={() => <DropdownMulti
                  {...filterConfig.fundType}
                  label={t(filterTypeLabelTextKey)}
                  autoTestId={generateAutoTestId?.(AutoTestIdPrefix.DROPDOWN, filterTypeLabelTextKey)}
                  optionListAutoTestId={generateAutoTestId?.(AutoTestIdPrefix.LIST, filterTypeLabelTextKey)}/>
              }
              renderAutocomplete={(filterConfig, options) => <Autocomplete
                  {...filterConfig.autocomplete}
                  onChange={(option, _event, _reason) => option && filterConfig.autocomplete.onChange(option)}
                  className={options.className}
                  mobileType={options.hasUnitCategorySelect ? 'small' : 'fake-input'}
                  searchPlaceholder={options.t(filterAutocompleteLabelTextKey)}
                  buttonPlaceholder={options.t(filterAutocompleteLabelTextKey)}
                  autoTestId={generateAutoTestId?.(AutoTestIdPrefix.BUTTON, filterAutocompleteLabelTextKey)}
              />}
              renderRiskDropdown={() => <DropdownMulti
                  {...filterConfig.risk}
                  autoTestId={generateAutoTestId(AutoTestIdPrefix.DROPDOWN, filterRiskLabelTextKey)}
                  optionListAutoTestId={generateAutoTestId(AutoTestIdPrefix.LIST, filterRiskLabelTextKey)}
                  label={t(filterRiskLabelTextKey)}/>
              }
              renderUmbrellaDropdown={() => <DropdownMulti
                  {...filterConfig.umbrella}
                  autoTestId={generateAutoTestId(AutoTestIdPrefix.DROPDOWN, filterUmbrellaLabelTextKey)}
                  optionListAutoTestId={generateAutoTestId(AutoTestIdPrefix.LIST, filterUmbrellaLabelTextKey)}
                  label={t(filterUmbrellaLabelTextKey)}
              />}
              renderHorizonDropdown={() => <DropdownMulti
                  {...filterConfig.horizon}
                  autoTestId={generateAutoTestId(AutoTestIdPrefix.DROPDOWN, filterHorizonLabelTextKey)}
                  optionListAutoTestId={generateAutoTestId(AutoTestIdPrefix.LIST, filterHorizonLabelTextKey)}
                  label={t(filterHorizonLabelTextKey)}
              />}
              isTestAutomationEnabled={testAutomationEnabled}
          />
          {otherFunds && otherFunds.length > 0 && (
              <StandardSortPanel
                  handleSelectRateKey={handleSelectRateKeyWithLogEvent}
                  sortConfig={sortConfig}
                  requestSort={requestSort}
                  rateKey={rateKey}
                  hasUnitCategorySelect={false}
                  withActionButton={true}
                  isTestAutomationEnabled={testAutomationEnabled}
                  generateAutoTestId={generateAutoTestId}
              />
          )}
          {specialPensionFunds &&
            <>
              <AlertBox title={t('pension-funds-info')} colorStyle="blue" className={c.pensionFundsInfoAlert}/>
              <FundList
                {...fundListConfig}
                funds={specialPensionFunds}
                renderFundDetailsDialog={(fund, unitCategoryCode, index, options) =>
                  <FundDetailsButtonWithDialog
                    fund={fund}
                    productType={productType}
                    buttonLabel={fund.name}
                    source={Screen.SELECT_FUNDS}
                    index={index}
                    changeRateKey={rateKey}
                    customButton={options.customButton}
                    className={options.className}
                    titleAsButton={options.titleAsButton}
                    onSelectFund={onSelectFund}
                    isFundSelected={options.isSelected}
                  />
                }
              />
              <AlertBox title={t('remaining-funds')} colorStyle="blue" className={c.remainingFundsAlert}/>
            </>
          }
          {otherFunds &&
            <FundList
              {...fundListConfig}
              funds={otherFunds}
              renderFundDetailsDialog={(fund, _unitCategoryCode, index, options) =>
                <FundDetailsButtonWithDialog
                  fund={fund}
                  productType={productType}
                  buttonLabel={fund.name}
                  source={Screen.SELECT_FUNDS}
                  index={index}
                  customButton={options.customButton}
                  className={options.className}
                  titleAsButton={options.titleAsButton}
                  onSelectFund={onSelectFund}
                  changeRateKey={rateKey}
                  isFundSelected={options.isSelected}
                />
              }
            />
          }
        </div>
      </div>
      {!isTransfer && <CartQuantityBar source={Screen.SELECT_FUNDS}/>}
    </>
  );
};



export default SelectFunds;
